// Review queue for candidate multi-leg trips -- see
// supabase/migrations/0046_multi_leg_groups.sql and src/lib/multiLegGroups.ts
// for the detection logic. Candidates are computed live every time this
// loads, as time-windowed clusters per account. Those clusters are only a
// SUGGESTION for the default tick state -- this screen flattens every
// candidate leg for an account onto one card with its own checkbox, so an
// admin can tick across two clusters the algorithm split apart (e.g. a real
// 6-stop job that shows up as two 3-leg clusters because one gap between
// stops was wider than the detection window) and confirm them as a single
// group, or equally split one cluster into separate groups by unticking.
// Only a confirmed group in multi_leg_groups actually changes capacity
// behaviour, consumed by both placeUnassignedJobs
// (src/components/fleet-capacity/slotSheetUtils.ts) and
// calculate-capacity-slot's unassigned-prebooking loop.
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { RefreshCw, Route, X } from "lucide-react";
import { formatPassengerName } from "@/lib/passengerName";
import {
  MultiLegCandidateLeg,
  loadMultiLegSettings,
  fetchMultiLegCandidates,
  confirmMultiLegGroup,
  rejectMultiLegGroup,
} from "@/lib/multiLegGroups";

interface MultiLegReviewProps {
  dateStr: string;
  onGroupConfirmed?: () => void;
}

interface AccountGroup {
  account_id: string;
  account_name: string | null;
  legs: MultiLegCandidateLeg[];
}

const formatTime = (iso: string | null) => (iso ? format(new Date(iso), "h:mm a") : "--");

const MultiLegReview: React.FC<MultiLegReviewProps> = ({ dateStr, onGroupConfirmed }) => {
  const [windowMinutes, setWindowMinutes] = useState(20);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [busyAccount, setBusyAccount] = useState<string | null>(null);

  const applyCandidates = (list: Awaited<ReturnType<typeof fetchMultiLegCandidates>>) => {
    // Flatten every cluster onto one card per account, deduping legs that
    // appear in more than one cluster.
    const byAccount = new Map<string, AccountGroup>();
    list.forEach((c) => {
      let g = byAccount.get(c.account_id);
      if (!g) {
        g = { account_id: c.account_id, account_name: c.account_name, legs: [] };
        byAccount.set(c.account_id, g);
      }
      c.legs.forEach((leg) => {
        if (!g!.legs.some((l) => l.job_id === leg.job_id)) g!.legs.push(leg);
      });
    });
    const nextGroups = Array.from(byAccount.values()).map((g) => ({
      ...g,
      legs: [...g.legs].sort(
        (a, b) => new Date(a.requested_pickup_at || 0).getTime() - new Date(b.requested_pickup_at || 0).getTime()
      ),
    }));
    setGroups(nextGroups);
    // Every leg shown already belongs to at least one 2+-leg cluster, so
    // default every checkbox to ticked -- the common case is just "confirm".
    const nextSelected: Record<string, Set<string>> = {};
    nextGroups.forEach((g) => {
      nextSelected[g.account_id] = new Set(g.legs.map((l) => l.job_id));
    });
    setSelected(nextSelected);
  };

  const reload = async (windowOverride?: number) => {
    setLoading(true);
    const list = await fetchMultiLegCandidates(dateStr, windowOverride ?? windowMinutes);
    applyCandidates(list);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const settings = await loadMultiLegSettings();
      setWindowMinutes(settings.detectionWindowMinutes);
      setLoading(true);
      const list = await fetchMultiLegCandidates(dateStr, settings.detectionWindowMinutes);
      applyCandidates(list);
      setLoading(false);
    })();
  }, [dateStr]);

  const toggleLeg = (accountId: string, jobId: string) => {
    setSelected((prev) => {
      const next = new Set(prev[accountId] || []);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return { ...prev, [accountId]: next };
    });
  };

  // Removes legs from a card once they've been confirmed or dismissed; drops
  // the whole card once fewer than 2 legs remain (nothing left to group).
  const removeLegs = (accountId: string, jobIds: string[]) => {
    setGroups((prev) =>
      prev
        .map((g) => (g.account_id === accountId ? { ...g, legs: g.legs.filter((l) => !jobIds.includes(l.job_id)) } : g))
        .filter((g) => g.legs.length >= 2)
    );
    setSelected((prev) => {
      const next = new Set(prev[accountId] || []);
      jobIds.forEach((id) => next.delete(id));
      return { ...prev, [accountId]: next };
    });
  };

  const handleConfirmSelected = async (g: AccountGroup) => {
    const jobIds = Array.from(selected[g.account_id] || []);
    if (jobIds.length < 2) {
      toast({ title: "Tick at least 2 legs", description: "A multi-leg group needs 2 or more stops.", variant: "destructive" });
      return;
    }
    setBusyAccount(g.account_id);
    try {
      await confirmMultiLegGroup(g.account_id, jobIds);
      toast({ title: "Confirmed", description: `${jobIds.length} legs grouped onto one vehicle.` });
      removeLegs(g.account_id, jobIds);
      onGroupConfirmed?.();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setBusyAccount(null);
  };

  const handleDismissLeg = async (g: AccountGroup, jobId: string) => {
    setBusyAccount(g.account_id);
    try {
      await rejectMultiLegGroup(g.account_id, [jobId]);
      removeLegs(g.account_id, [jobId]);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setBusyAccount(null);
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
        <CardTitle className="text-xl text-primary flex items-center gap-2">
          <Route className="w-5 h-5" />
          Multi-Leg Trips
        </CardTitle>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Window</Label>
          <Input
            type="number"
            value={windowMinutes}
            onChange={(e) => setWindowMinutes(parseInt(e.target.value, 10) || 0)}
            onBlur={() => reload()}
            className="w-16 h-8"
          />
          <span className="text-xs text-muted-foreground">min</span>
          <Button variant="outline" size="sm" onClick={() => reload()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Bookings on the same account, close together in time, that might really be one vehicle doing several
          stops rather than separate simultaneous cars. Every leg is ticked by default -- untick any that don't
          belong, or tick across what looks like two separate groups below to merge them into one (e.g. a real
          6-stop job the detector split into two 3-leg groups because of a gap wider than the window). Confirm
          only changes the capacity sheet and live IVR engine once you hit the button.
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No multi-leg candidates for this day.</p>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const selectedCount = selected[g.account_id]?.size ?? 0;
              const busy = busyAccount === g.account_id;
              return (
                <div key={g.account_id} className="rounded-lg border border-yellow-900/30 bg-yellow-950/10 p-3 space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm font-medium text-yellow-500">
                      {g.account_name || `Account ${g.account_id}`} -- {g.legs.length} legs
                    </p>
                    <Button size="sm" onClick={() => handleConfirmSelected(g)} disabled={busy || selectedCount < 2}>
                      Confirm {selectedCount} as One Vehicle
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {g.legs.map((leg) => {
                      const checked = selected[g.account_id]?.has(leg.job_id) ?? false;
                      return (
                        <div key={leg.job_id} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Checkbox
                            checked={checked}
                            disabled={busy}
                            onCheckedChange={() => toggleLeg(g.account_id, leg.job_id)}
                          />
                          <span className="flex-1">
                            {formatTime(leg.requested_pickup_at)} --{" "}
                            {formatPassengerName(leg.passenger_first_name, leg.passenger_last_name) || "Unknown passenger"}:{" "}
                            {leg.pick_up_location || "?"} → {leg.drop_off_location || "?"}
                          </span>
                          <button
                            type="button"
                            title="Not part of any multi-leg group"
                            disabled={busy}
                            onClick={() => handleDismissLeg(g, leg.job_id)}
                            className="text-muted-foreground hover:text-red-400 disabled:opacity-50"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MultiLegReview;
